import type {
  CreateUserRequest,
  ListUsersRequest,
  ListUsersResponse,
  UpdatePasswordRequest,
  UpdateUserRequest,
  User,
  UserId,
} from "metabase-types/api";

import { Api } from "./api";
import {
  idTag,
  invalidateTags,
  listTag,
  provideUserListTags,
  provideUserTags,
} from "./tags";

export const userApi = Api.injectEndpoints({
  endpoints: (builder) => ({
    listUsers: builder.query<ListUsersResponse, ListUsersRequest | void>({
      query: (params) => ({
        method: "GET",
        url: "/api/user",
        params,
      }),
      providesTags: (response) =>
        response ? provideUserListTags(response.data) : [],
    }),
    listUserRecipients: builder.query<ListUsersResponse, void>({
      query: () => ({
        method: "GET",
        url: "/api/user/recipients",
      }),
      providesTags: (response) =>
        response ? provideUserListTags(response.data) : [],
    }),
    getUser: builder.query<User, UserId>({
      query: (id) => ({
        method: "GET",
        url: `/api/user/${id}`,
      }),
      providesTags: (user) => (user ? provideUserTags(user) : []),
    }),
    getCurrentUser: builder.query<User, void>({
      query: () => ({
        method: "GET",
        url: "/api/user/current",
      }),
      providesTags: (user) => (user ? provideUserTags(user) : []),
    }),
    createUser: builder.mutation<User, CreateUserRequest>({
      query: (body) => ({
        method: "POST",
        url: "/api/user",
        body,
      }),
      invalidatesTags: (_, error) =>
        invalidateTags(error, [
          listTag("user"),
          listTag("tenant"),
          listTag("permissions-group"),
        ]),
    }),
    updatePassword: builder.mutation<void, UpdatePasswordRequest>({
      query: ({ id, old_password, password }) => ({
        method: "PUT",
        url: `/api/user/${id}/password`,
        body: { old_password, password },
      }),
      invalidatesTags: (_, error, { id }) =>
        invalidateTags(error, [listTag("user"), idTag("user", id)]),
    }),
    deactivateUser: builder.mutation<void, UserId>({
      query: (id) => ({
        method: "DELETE",
        url: `/api/user/${id}`,
      }),
      invalidatesTags: (_, error, id) =>
        invalidateTags(error, [
          listTag("user"),
          idTag("user", id),
          listTag("permissions-group"),
        ]),
    }),
    reactivateUser: builder.mutation<User, UserId>({
      query: (id) => ({
        method: "PUT",
        url: `/api/user/${id}/reactivate`,
      }),
      invalidatesTags: (_, error, id) =>
        invalidateTags(error, [
          listTag("user"),
          idTag("user", id),
          listTag("permissions-group"),
        ]),
    }),
    updateUser: builder.mutation<User, UpdateUserRequest>({
      query: ({ id, ...body }) => ({
        method: "PUT",
        url: `/api/user/${id}`,
        body,
      }),
      invalidatesTags: (_, error, { id }) =>
        invalidateTags(error, [listTag("user"), idTag("user", id)]),
    }),
    getSms2fa: builder.query<
      { sms_2fa_phone: string | null; sms_2fa_available: boolean },
      UserId
    >({
      query: (id) => ({ method: "GET", url: `/api/user/${id}/sms-2fa` }),
      providesTags: (_r, _e, id) => [idTag("user", id)],
    }),
    updateSms2fa: builder.mutation<
      { sms_2fa_phone: string | null },
      { id: UserId; sms_2fa_phone: string | null }
    >({
      query: ({ id, sms_2fa_phone }) => ({
        method: "PUT",
        url: `/api/user/${id}/sms-2fa`,
        body: { sms_2fa_phone },
      }),
      invalidatesTags: (_r, error, { id }) =>
        invalidateTags(error, [idTag("user", id)]),
    }),
    listUserAttributes: builder.query<string[], void>({
      query: () => "/api/mt/user/attributes",
      providesTags: (response) => (response ? [listTag("user")] : []),
    }),
    updateUserModalQbnewb: builder.mutation<void, UserId>({
      query: (id) => ({
        method: "PUT",
        url: `/api/user/${id}/modal/qbnewb`,
      }),
      invalidatesTags: (_, error, id) =>
        invalidateTags(error, [idTag("user", id)]),
    }),
  }),
});

export const {
  useListUsersQuery,
  useListUserRecipientsQuery,
  useGetUserQuery,
  useGetCurrentUserQuery,
  useCreateUserMutation,
  useUpdatePasswordMutation,
  useDeactivateUserMutation,
  useReactivateUserMutation,
  useUpdateUserMutation,
  useGetSms2faQuery,
  useUpdateSms2faMutation,
  useListUserAttributesQuery,
  useUpdateUserModalQbnewbMutation,
} = userApi;
